import { create } from 'zustand';

import { clearPeopleScopeStorage } from '@/stores/peopleScopeStore';
import { type User } from '@/types/auth';

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'elixaa_access_token',
  REFRESH_TOKEN: 'elixaa_refresh_token',
  ID_TOKEN: 'elixaa_id_token',
  USER: 'elixaa_user',
  PERSIST_MODE: 'elixaa_persist_mode',
} as const;

export type PersistMode = 'session' | 'local';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  idToken: string | null;
  persistMode: PersistMode;
  mfaSession: string | null;
  mfaRequired: boolean;
  mfaSetupRequired: boolean;
}

interface AuthActions {
  setAuth: (p: {
    user: User;
    accessToken: string;
    refreshToken: string;
    idToken: string;
    persist?: PersistMode;
  }) => void;
  setUser: (u: User) => void;
  setTokens: (p: { accessToken: string; idToken: string }) => void;
  setMfaChallenge: (session: string) => void;
  setMfaSetupRequired: (session: string) => void;
  clearMfa: () => void;
  clearAuth: () => void;
  rehydrate: () => void;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  idToken: null,
  persistMode: 'session',
  mfaSession: null,
  mfaRequired: false,
  mfaSetupRequired: false,
};

function storageFor(mode: PersistMode): Storage {
  return mode === 'local' ? window.localStorage : window.sessionStorage;
}

function writeToStorage(
  mode: PersistMode,
  payload: {
    user: User;
    accessToken: string;
    refreshToken: string;
    idToken: string;
  },
) {
  const s = storageFor(mode);
  s.setItem(STORAGE_KEYS.ACCESS_TOKEN, payload.accessToken);
  s.setItem(STORAGE_KEYS.REFRESH_TOKEN, payload.refreshToken);
  s.setItem(STORAGE_KEYS.ID_TOKEN, payload.idToken);
  s.setItem(STORAGE_KEYS.USER, JSON.stringify(payload.user));
  window.localStorage.setItem(STORAGE_KEYS.PERSIST_MODE, mode);
}

function clearBothStorages() {
  [window.localStorage, window.sessionStorage].forEach((s) => {
    s.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    s.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    s.removeItem(STORAGE_KEYS.ID_TOKEN);
    s.removeItem(STORAGE_KEYS.USER);
  });
  window.localStorage.removeItem(STORAGE_KEYS.PERSIST_MODE);
  // The People scope (org/location) is a per-user selection persisted outside
  // this store. Clearing it here — the single choke point every teardown path
  // funnels through (clearAuthStorage, clearAuth, setMfaChallenge,
  // setMfaSetupRequired) — stops one user's org leaking into the next user's
  // session on a shared browser. Storage-only, same no-flash contract as the
  // token wipe above.
  clearPeopleScopeStorage();
}

/**
 * Clear persisted auth tokens from local + session storage WITHOUT mutating
 * the in-memory React store.
 *
 * Use this when logging out via a full-page navigation (`window.location.*`):
 * mutating zustand state on the still-mounted protected page would trigger
 * synchronous re-renders before the browser unloads, briefly painting the
 * page with `user: null` (sidebar collapses to Home-only, greeting reads
 * "Good morning, there.", etc.). The full reload wipes in-memory state
 * cleanly — only storage needs proactive clearing so the next page load
 * doesn't rehydrate the dead session.
 */
export function clearAuthStorage(): void {
  clearBothStorages();
}

export const useAuthStore = create<AuthStore>((set) => ({
  ...initialState,

  setAuth: ({ user, accessToken, refreshToken, idToken, persist = 'session' }) => {
    writeToStorage(persist, { user, accessToken, refreshToken, idToken });
    set({
      isAuthenticated: true,
      user,
      accessToken,
      refreshToken,
      idToken,
      persistMode: persist,
      mfaSession: null,
      mfaRequired: false,
      mfaSetupRequired: false,
    });
  },

  setUser: (user) => {
    const mode =
      (window.localStorage.getItem(STORAGE_KEYS.PERSIST_MODE) as PersistMode | null) ?? 'session';
    storageFor(mode).setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    set({ user });
  },

  setTokens: ({ accessToken, idToken }) => {
    // Refresh response carries no refreshToken — only rotate access + id.
    // No-op when there is no active session, so a late-firing timer after
    // logout cannot resurrect auth state.
    const { refreshToken: currentRefresh, persistMode } = useAuthStore.getState();
    if (!currentRefresh) return;
    const s = storageFor(persistMode);
    s.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    s.setItem(STORAGE_KEYS.ID_TOKEN, idToken);
    set({ accessToken, idToken });
  },

  setMfaChallenge: (session) => {
    clearBothStorages();
    set({
      ...initialState,
      mfaRequired: true,
      mfaSetupRequired: false,
      mfaSession: session,
    });
  },
  setMfaSetupRequired: (session) => {
    clearBothStorages();
    set({
      ...initialState,
      mfaSetupRequired: true,
      mfaRequired: false,
      mfaSession: session,
    });
  },
  clearMfa: () => {
    set({ mfaSession: null, mfaRequired: false, mfaSetupRequired: false });
  },

  clearAuth: () => {
    // Note: this does NOT cancel any pending proactive-refresh timer; doing
    // so via a dynamic import here triggers TDZ races in tests that load
    // authStore before tokenRefresh. Every production path that wipes auth
    // (useLogout, coalescedRefresh failure, IdleLogoutProvider lockout,
    // crossTabAuth, api-client 401 fallback) calls cancelProactiveRefresh()
    // explicitly. If a stale timer does fire after clearAuth, it hits
    // /auth/refresh with a dead token and the 401 fallback handles it.
    clearBothStorages();
    set(initialState);
  },

  rehydrate: () => {
    const preferredMode =
      (window.localStorage.getItem(STORAGE_KEYS.PERSIST_MODE) as PersistMode | null) ?? 'session';
    const sources: PersistMode[] =
      preferredMode === 'local' ? ['local', 'session'] : ['session', 'local'];
    for (const mode of sources) {
      const s = storageFor(mode);
      const accessToken = s.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = s.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      const idToken = s.getItem(STORAGE_KEYS.ID_TOKEN);
      const userJson = s.getItem(STORAGE_KEYS.USER);
      if (accessToken && refreshToken && idToken && userJson) {
        try {
          const user = JSON.parse(userJson) as User;
          set({
            isAuthenticated: true,
            user,
            accessToken,
            refreshToken,
            idToken,
            persistMode: mode,
          });
          return;
        } catch {
          // fall through
        }
      }
    }
  },
}));
